export class PollingTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Polling timed out after ${timeoutMs / 1000}s without meeting the expected condition`);
    this.name = 'PollingTimeoutError';
  }
}

export const forCondition = async <T>(
  action: () => Promise<T>,
  predicate: (input: T) => boolean,
  pollingInterval = 3000,
  overallTimeout = 300_000
): Promise<T> => {
  let done = false;
  let timeoutId: ReturnType<typeof setTimeout>;

  const poller = async (): Promise<T> => {
    let value: T = await action();

    while (!done && !predicate(value)) {
      await delay(pollingInterval);
      if (done) break;
      value = await action();
    }

    return value;
  };

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new PollingTimeoutError(overallTimeout)),
      overallTimeout
    );
  });

  try {
    return await Promise.race([poller(), timeoutPromise]);
  } finally {
    clearTimeout(timeoutId!);
    done = true;
  }
};

const delay = (duration: number): Promise<void> =>
  new Promise<void>((resolve) => setTimeout(resolve, duration));
